import { useState, useEffect, useRef, useCallback } from 'react';
import type { AIMode } from '@/hooks/useAIAgentMode';
import type { Message } from '@/types/message-types';

interface UseAIProcessingStateProps {
  aiMode: AIMode;
  messages: Message[];
  conversationId: string;
  onProcessingStart?: (messageContent?: string) => void;
}

export function useAIProcessingState({ aiMode, messages, conversationId, onProcessingStart }: UseAIProcessingStateProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const lastProcessedMessageId = useRef<string | null>(null);
  const processingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Reset everything when conversation changes
  useEffect(() => {
    console.log(`[AIProcessingState] 🔄 Conversation changed to: ${conversationId || 'none'}`);
    setIsProcessing(false);
    lastProcessedMessageId.current = null;
    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current);
      processingTimeoutRef.current = null;
    }
  }, [conversationId]);

  // Check for AI responses that should stop the animation
  useEffect(() => {
    if (!messages || !isProcessing) return;

    const newestMessage = messages[messages.length - 1];
    if (newestMessage?.sender_type && 
        newestMessage.sender_type !== 'customer' &&
        newestMessage.id !== lastProcessedMessageId.current) {
      
      console.log(`[AIProcessingState] 🤖 AI response detected, stopping animation:`, {
        messageId: newestMessage.id,
        senderType: newestMessage.sender_type,
        content: newestMessage.content?.substring(0, 50) + '...',
        timestamp: new Date().toISOString()
      });
      setIsProcessing(false);
      lastProcessedMessageId.current = newestMessage.id;
      
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
        processingTimeoutRef.current = null;
      }
    }
  }, [messages, isProcessing]);

  // Stop processing when AI mode changes to manual
  useEffect(() => {
    if (aiMode === 'manual' && isProcessing) {
      console.log(`[AIProcessingState] 🔄 AI mode changed to manual, stopping animation`);
      setIsProcessing(false);
      
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
        processingTimeoutRef.current = null;
      }
    }
  }, [aiMode, isProcessing]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
    };
  }, []);

  // Manual start processing function
  const startProcessing = useCallback((messageContent?: string) => {
    console.log(`[AIProcessingState] 🚀 startProcessing called:`, {
      aiMode,
      conversationId,
      canStart: aiMode === 'auto' || aiMode === 'suggestions',
      currentlyProcessing: isProcessing,
      messageContent: messageContent?.substring(0, 50) + '...',
      timestamp: new Date().toISOString()
    });
    
    if (aiMode === 'auto' || aiMode === 'suggestions') {
      console.log(`[AIProcessingState] ✅ Starting AI processing animation (${aiMode} mode)`);
      setIsProcessing(true);
      
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
      
      // Increased timeout to 45 seconds to match useAIAgentMode timeout
      processingTimeoutRef.current = setTimeout(() => {
        console.log(`[AIProcessingState] ⏰ Processing timeout reached, stopping animation`, {
          conversationId,
          aiMode,
          messageContent: messageContent?.substring(0, 50),
          timestamp: new Date().toISOString()
        });
        setIsProcessing(false);
        
        // Show error toast for timeout
        if (typeof window !== 'undefined' && window.dispatchEvent) {
          window.dispatchEvent(new CustomEvent('ai-processing-timeout', {
            detail: { conversationId, aiMode, messageContent }
          }));
        }
      }, 45000); // Match the 45-second timeout from useAIAgentMode

      // Call the onProcessingStart callback to trigger actual AI suggestion generation
      if (onProcessingStart) {
        console.log(`[AIProcessingState] 🤖 Calling onProcessingStart callback for ${aiMode} mode`);
        try {
          onProcessingStart(messageContent);
        } catch (error) {
          console.error(`[AIProcessingState] ❌ Error in onProcessingStart:`, error);
          setIsProcessing(false);
          if (processingTimeoutRef.current) {
            clearTimeout(processingTimeoutRef.current);
            processingTimeoutRef.current = null;
          }
        }
      }
    } else {
      console.log(`[AIProcessingState] ❌ Cannot start processing - aiMode is ${aiMode}, needs to be 'auto' or 'suggestions'`);
    }
  }, [aiMode, conversationId, isProcessing, onProcessingStart]);

  // Manual stop processing function
  const stopProcessing = useCallback(() => {
    console.log(`[AIProcessingState] 🛑 Manual stop processing called`);
    setIsProcessing(false);
    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current);
      processingTimeoutRef.current = null;
    }
  }, []);

  return {
    isProcessing,
    startProcessing,
    stopProcessing
  };
}